import type { CustomerSummary, DocumentRef, PolicyRow } from "@agency/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { PolicySheet } from "../components/PolicySheet";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Select, Sheet } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { formatPhone, money, relativeDays, shortDate } from "../theme";

const STATUSES = ["active", "pending_cancel", "cancelled", "expired", "renewed", "bound", "quoted"] as const;

export function PoliciesScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const { toast } = useToast();
  const [status, setStatus] = useState("");
  const [carrier, setCarrier] = useState("");
  const [line, setLine] = useState("");
  const [search, setSearch] = useState("");
  const [pickOpen, setPickOpen] = useState(false);
  const [pickQ, setPickQ] = useState("");
  const [forCustomer, setForCustomer] = useState<{ id: string; name: string } | null>(null);
  const pick = useQuery({ queryKey: ["search", pickQ], queryFn: async () => (await callAction<"agency.customer.search", CustomerSummary[]>("agency.customer.search", { q: pickQ, limit: 8 })).result, enabled: pickOpen && pickQ.trim().length >= 2 });

  const q = useQuery({
    queryKey: ["policies", status, carrier, line],
    queryFn: async () => (await callAction<"agency.policy.list", PolicyRow[]>("agency.policy.list", { status: status || undefined, carrier: carrier || undefined, line: line || undefined, limit: 1000 })).result,
  });
  const exp = useMutation({
    mutationFn: () => callAction<"agency.export.create", DocumentRef>("agency.export.create", { view: "book_of_business", format: "csv" }),
    onSuccess: (r) => {
      toast({ title: t("common.exported"), tone: "success" });
      window.open(r.result.url, "_blank", "noopener");
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = q.data ?? [];
    return (s ? list.filter((r) => r.customerName.toLowerCase().includes(s) || r.policy.policyNumber.toLowerCase().includes(s)) : list).sort((a, b) => a.policy.expirationDate.localeCompare(b.policy.expirationDate));
  }, [q.data, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("policies.title")}</h1>
          <p className="text-ink/60">{t("policies.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exp.mutate()} loading={exp.isPending}><Download className="h-4 w-4" />{t("common.export")}</Button>
          <Button onClick={() => { setPickQ(""); setPickOpen(true); }}><Plus className="h-4 w-4" />{t("policies.add")}</Button>
        </div>
      </div>

      <Card className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input label={t("common.search")} placeholder={t("policies.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select label={t("common.status")} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("common.all")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`policy.status.${s}`)}</option>)}
        </Select>
        <Select label={t("common.carrier")} value={carrier} onChange={(e) => setCarrier(e.target.value)}>
          <option value="">{t("common.all")}</option>
          {config.agency.carriers.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </Select>
        <Select label={t("common.line")} value={line} onChange={(e) => setLine(e.target.value)}>
          <option value="">{t("common.all")}</option>
          {config.agency.lines.map((l) => <option key={l} value={l}>{l.replace(/_/g, " ")}</option>)}
        </Select>
      </Card>

      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && rows.length === 0 ? <EmptyState title={t("policies.none")} /> : null}
      {rows.length > 0 ? (
        <Card className="overflow-x-auto">
          <p className="px-4 pt-3 text-xs text-ink/55">{rows.length} {t("policies.count")}</p>
          <table className="w-full text-sm">
            <thead className="text-left text-ink/55">
              <tr>
                <th className="px-4 py-3">{t("common.name")}</th>
                <th className="px-4 py-3">{t("common.carrier")}</th>
                <th className="px-4 py-3 hidden sm:table-cell">{t("common.line")}</th>
                <th className="px-4 py-3 hidden md:table-cell">#</th>
                <th className="px-4 py-3">{t("common.status")}</th>
                <th className="px-4 py-3 hidden lg:table-cell">{t("policies.effective")}</th>
                <th className="px-4 py-3">{t("common.expires")}</th>
                <th className="px-4 py-3">{t("common.premium")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("policies.source")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.policy.id} className="border-t border-ink/5">
                  <td className="px-4 py-2">
                    <Link to={`/customers/${r.customerId}`} className="font-semibold hover:underline block min-h-[36px] leading-[36px]">{r.customerName}</Link>
                  </td>
                  <td className="px-4 py-2">{r.policy.carrier}<span className="sm:hidden block text-xs text-ink/50">{r.policy.line}</span></td>
                  <td className="px-4 py-2 hidden sm:table-cell">{r.policy.line.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2 hidden md:table-cell font-mono text-xs">{r.policy.policyNumber}</td>
                  <td className="px-4 py-2"><Badge tone={r.policy.status === "active" ? "green" : r.policy.status === "cancelled" || r.policy.status === "expired" ? "neutral" : "amber"}>{t(`policy.status.${r.policy.status}`)}</Badge></td>
                  <td className="px-4 py-2 hidden lg:table-cell">{shortDate(r.policy.effectiveDate, locale)}</td>
                  <td className="px-4 py-2">{shortDate(r.policy.expirationDate, locale)}<span className="block text-xs text-ink/50">{relativeDays(r.daysUntil, locale)}</span></td>
                  <td className="px-4 py-2">{money(r.policy.premiumCents)}</td>
                  <td className="px-4 py-2 hidden md:table-cell"><Badge>{t(`policies.source.${r.policy.source}`)}</Badge>{Object.keys(r.policy.externalIds ?? {}).length ? <span className="block text-[11px] text-ink/45 font-mono">{Object.entries(r.policy.externalIds).map(([k, v]) => `${k}:${v}`).join(" ")}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Sheet open={pickOpen} onClose={() => setPickOpen(false)} title={t("policies.add")}>
        <p className="font-semibold">{t("policies.pickCustomer")}</p>
        <p className="text-sm text-ink/55 mb-3">{t("policies.pickCustomerHelp")}</p>
        <Input autoFocus value={pickQ} onChange={(e) => setPickQ(e.target.value)} placeholder={t("customers.search")} aria-label={t("customers.search")} />
        <ul className="mt-3 divide-y divide-ink/5">
          {pick.isLoading ? <li className="py-2 text-sm text-ink/55">{t("common.loading")}</li> : null}
          {pick.data?.length === 0 ? <li className="py-2 text-sm text-ink/55">{t("customers.none")}</li> : null}
          {pick.data?.map((c) => (
            <li key={c.id}>
              <button className="flex w-full items-center justify-between gap-3 min-h-tap px-2 text-left rounded-xl hover:bg-primary/8" onClick={() => { setForCustomer({ id: c.id, name: c.name }); setPickOpen(false); }}>
                <span className="font-semibold">{c.name}</span>
                <span className="text-xs text-ink/55">{formatPhone(c.phone) ?? c.city ?? ""} · {c.policyCount} {t("common.policies").toLowerCase()}</span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
      {forCustomer ? <PolicySheet open={!!forCustomer} onClose={() => setForCustomer(null)} customerId={forCustomer.id} onSaved={() => q.refetch()} /> : null}
    </div>
  );
}
